//#########################################################################
//
// This service contains all the states and functions related to the block-based
// environment
//
//#########################################################################
import {Injectable} from '@angular/core';

import {InputComponent} from '../components/input.component';

declare var Blockly: any;

@Injectable()
export class BlocksService {

  constructor() { }

  private command = '';
  private mode = 3;
  private workspace;
  private currentState;
  private csActive = false;
  private historyIsGray = false;
  private copyableHistory:Element = this.getInitialEmptyHistory();

  isHistoryGray(){
    return this.historyIsGray;
  }

  colorHistoryGray() {
    // make history blocks gray
    var blocks = this.getWorkspace().getBlockById("history");
    if (blocks!=null){
      blocks.getDescendants().slice(1 /* skip start block */).forEach(element => { element.setColour("#efefef"); });
    }

    this.historyIsGray = true;
  }

  getCommand(){
    return this.command;
  }

  setCommand(command){
    this.command = command;
  }

  getMode(){
    return this.mode;
  }

  // 1: BlueBot, 2: Blocks with Input, 3: TextMode/No BlockMode
  setMode(mode){
    this.mode = mode;
  }

  isBlockMode(){
    return (this.mode==3)?0:1;
  }

  isBlueBotMode(){
    return (this.mode==1)?1:0;
  }

  setWorkspace(workspace){
    this.workspace = workspace;
  }

  getWorkspace(){
    return this.workspace;
  }

  enableClearScreenBeforeExec() {
    this.csActive = true;
  }

  disableClearScreenBeforeExec() {
    this.csActive = false;
  }

  isClearScreenBeforeExec() {
    return this.csActive;
  }

  /*
   * TOOLBOXES
   * 1) Blue-Bot 2) variable number of steps Blocks
   */

  getBlueBotToolbox(){
    let toolbox = '<xml>';
    // start and cs - removed: start is displayed automatically, cs is executed automatically
    //toolbox += '  <block type="event_greenflag"></block>'
    //toolbox += '  <block type="clear_screen"></block>'
    // move fixed step size
    toolbox += '  <block type="move_forward_100"></block>';
    toolbox += '  <block type="move_back_100"></block>';
    // turn fixed 90 degrees
    toolbox += '  <block type="turn_left_90"></block>';
    toolbox += '  <block type="turn_right_90"></block>';
    // wait
    //toolbox += '<block type="wait_100"></block>';
    // set pen color
    toolbox += '<block type="set_color">'
    toolbox += '<value name="CHOICE">';
    toolbox += '<shadow type="dropdown_color">'
    toolbox += '<field name="CHOICE">black</field>'
    toolbox += '</shadow></value></block>';
    // repeat
    toolbox += '  <block type="control_repeat">';
    toolbox += '  <value name="TIMES"><shadow type="math_whole_number">';
    toolbox += '  <field name="NUM">4</field>';
    toolbox += '  </shadow></value></block>';
    // close xml
    toolbox += '  </xml>';
    return toolbox;
  }

  getVariant2Toolbox(){
    let toolbox = '<xml>';
    // start and cs - removed: start is displayed automatically, cs is executed automatically
    //toolbox += '  <block type="event_greenflag"></block>'
    //toolbox += '  <block type="clear_screen"></block>'
    // move forward
    toolbox += '  <block type="move_forward">';
    toolbox += '  <value name="TIMES"><shadow type="math_whole_number">';
    toolbox += '  <field name="NUM">100</field>';
    toolbox += '  </shadow></value></block>';
    //move back
    toolbox += '  <block type="move_back">';
    toolbox += '  <value name="TIMES"><shadow type="math_whole_number">';
    toolbox += '  <field name="NUM">100</field>';
    toolbox += '  </shadow></value></block>';
    // turn right
    toolbox += '  <block type="turn_left">';
    toolbox += '  <value name="TIMES"><shadow type="math_angle">';
    toolbox += '  <field name="NUM">90</field>';
    toolbox += '  </shadow></value></block>';
    //turn left
    toolbox += '  <block type="turn_right">';
    toolbox += '  <value name="TIMES"><shadow type="math_angle">';
    toolbox += '  <field name="NUM">90</field>';
    toolbox += '  </shadow></value></block>';
    // wait
    /*
    toolbox += '  <block type="wait">';
    toolbox += '  <value name="TIMES"><shadow type="math_whole_number">';
    toolbox += '  <field name="NUM">100</field>';
    toolbox += '  </shadow></value></block>';
    */
    // set pen color
    toolbox += '<block type="set_color">'
    toolbox += '<value name="CHOICE">';
    toolbox += '<shadow type="dropdown_color">'
    toolbox += '<field name="CHOICE">black</field>'
    toolbox += '</shadow></value></block>';
    // repeat
    toolbox += '  <block type="control_repeat">';
    toolbox += '  <value name="TIMES"><shadow type="math_whole_number">';
    toolbox += '  <field name="NUM">4</field>';
    toolbox += '  </shadow></value></block>';
    // close xml
    toolbox += '  </xml>';
    return toolbox;
  }

  /*
   * SETUP AND MODIFY WORKSPACE
   */

  // returns the DOM of a workspace with empty commandList and the specified history
  makeWorkspaceWithHistory(history){
    // set attribute movable="false" (implies not deletable but add flag deletable="false" as well)
    //  xmlns=\"http://www.w3.org/1999/xhtml\"
    var allExceptHistory = Blockly.Xml.textToDom("<xml><variables></variables><block type=\"event_greenflag\" deletable=\"false\" movable=\"false\" id=\"F}H4nob:$c`A%*7:^99Z\" x=\"50\" y=\"25\"></block></xml>");
    allExceptHistory.appendChild(history)
    return allExceptHistory;
  }

  // XML DOM of the initial History.
  getInitialEmptyHistory(){
    return Blockly.Xml.textToDom("<block type=\"history\" movable=\"false\" id=\"history\" x=\"50\" y=\"122\" ></block>");
  }

  getInitialWorkspace() {
    return this.makeWorkspaceWithHistory(this.getInitialEmptyHistory());
  }

  clearWorkspace() {
    this.loadDOMasWorkspace(this.getInitialWorkspace());
  }

  // workaround function to upadte input fields in blocks after typing
  rerenderWorkspace() {
    this.saveWorkspace();
    this.loadWorkspace();
  }

  saveWorkspace() {
    this.currentState = Blockly.Xml.workspaceToDom(this.getWorkspace());
  }

  loadWorkspace() {
    this.loadDOMasWorkspace(this.currentState);
  }

  // given a DOM for the workspace, it loads that into the actual
  // workspace and makes adaptations to the history.
  loadDOMasWorkspace(xml){
    Blockly.Xml.clearWorkspaceAndLoadFromXml(xml, this.getWorkspace());
    if(this.historyIsGray){
      this.colorHistoryGray();
    }
    else {
      this.colorHistory();
    }
  }

  // copies all blocks in history to the exec-blocks.
  // they are added at the end of the exec-blocks.
  copyHistoryToExec(){
    var newDOM = Blockly.Xml.workspaceToDom(this.getWorkspace()).cloneNode(true);

    if(this.copyableHistory.children.length > 0){
      var history = this.copyableHistory.children[0].children[0].cloneNode(true); // skip the <next> block.
      var exec = newDOM.children[1];
      newDOM.replaceChild(this.withNewBlocksAtEnd(exec, history),exec);
    }

    this.loadDOMasWorkspace(newDOM);
  }

  addHistorytoExecCopyButton(){
    var div = <HTMLDivElement>document.getElementById("copyHistoryToExec").cloneNode(true);
    div.classList.remove('hidden');
    div.onclick = function(e){this.copyHistoryToExec();}.bind(this);
    document.getElementById("blocklyDiv").children[0].appendChild(div);
  }

  /*
   * READ, PARSE AND EXECUTE BLOCKS
   */

  xmlDomToCode(xml){
    var str = "";

    if (xml.localName==="xml"){ // outer most layer
      return this.xmlDomToCode(xml.children[1]) // "block"
    }
    else if (xml.localName==="next"){ // always proceed deeper at "next
      if (xml.children.length > 0) {
        return this.xmlDomToCode(xml.children[0]); // "block"
      }
    }
    else if (xml.localName==="block"){
      // for a default block, the last child is the <next> statement, if it exists.
      // for zero argument blocks (e.g. cs, move_forward_100, ...),
      // the first child (if it exists) is the next statement.
      // for one argument blocks (e.g. move_forward, turn_left, ...),
      // the second child (if it exists) is the next statement
      // special care is given "control_repeat",
      // as it has a SUBSTACK chlid if and only if the repeat has non-empty body
      // - and a <next> statement, if it was subsequent statements

      let ty = xml.attributes["type"].nodeValue; // "move_forward_100", "control_repeat", ...
      // console.log("<"+xml.localName+", #chld = "+xml.children.length+", ty="+ty+">");

      var hasNextStmt;
      var nextStmt;

      // collects what to do depending on the block-type
      let commandActions = [
         // block-type      ,nArgs, text-command   ,accessor for argument in children of block
         ["event_greenflag"  ,0, ""          ]
        ,["clear_screen"     ,0, " cs"       ]
        ,["move_forward_100" ,0, " fd 100"   ]
        ,["move_back_100"    ,0, " bk 100"   ]
        ,["turn_right_90"    ,0, " rt 90"    ]
        ,["turn_left_90"     ,0, " lt 90"    ]
        ,["wait_100"         ,0, " wait 100" ]
        ,["move_forward"     ,1, " fd ",    "TIMES" ]
        ,["move_back"        ,1, " bk ",    "TIMES" ]
        ,["turn_right"       ,1, " rt ",    "TIMES" ]
        ,["turn_left"        ,1, " lt ",    "TIMES" ]
        ,["wait"             ,1, " wait ",  "TIMES" ]
        ,["set_color"        ,1, " setpc ", "CHOICE"]
      ];

      let maybeAction = commandActions.filter(cmdAct => cmdAct[0] == ty);

      if (maybeAction.length >= 1){
        var [_,nArgs,cmd,accessor] = maybeAction[0];

        // if the command has arguments, then it is fetched from the xml children
        str += cmd + ( (nArgs == 1)?xml.children[accessor].innerText:"" );

        hasNextStmt = xml.children.length > nArgs;
        nextStmt = hasNextStmt?xml.children[nArgs]:null;
      }
      else if (ty == "control_repeat") {
        let nIter = xml.children.TIMES.innerText;
        let subStmts = xml.children.SUBSTACK?this.xmlDomToCode(xml.children.SUBSTACK.children[0]):"";

        str += " repeat " + nIter + " [ " + subStmts + " ]";

        let n = xml.children.length;
        hasNextStmt = (n == 1 /* TIMES */ + (xml.children.SUBSTACK?1:0) /* SUBSTACK */ + 1 /* next */);
        nextStmt = xml.children[1+(xml.children.SUBSTACK?1:0)];
      }
      else {
        console.log("WARNING. Unknown command type encountered: " + ty);
      }

      // continue with next statements
      if (hasNextStmt){
        let rec = this.xmlDomToCode(nextStmt);
        str += " " + rec;
      }

    }
    else {
      console.log("WARNING. Unkown node: "+ xml.localName);
    }
    return str;
  }

  readBlocks() {
    var xml = Blockly.Xml.workspaceToDom(this.getWorkspace());

    let command = (this.csActive?"cs ":"") + this.xmlDomToCode(xml);
    this.setCommand(command);
    console.log("Command: " + command);

    return [command,xml];
  }

  /*
   * HISTORY
   */

  // Uses old XML to make new XML DOM.
  moveExecBlocksToHistory(xml) {
    // get executed blocks. "event_greenflag" -> "next" -> "block"
    var newBlocks = xml.children[1].children[0].children[0];

    this.copyableHistory = this.withNewBlocksAtEnd(this.copyableHistory,newBlocks);

    var newDOM = this.makeWorkspaceWithHistory(this.copyableHistory);

    this.loadDOMasWorkspace(newDOM);
  }

  // recurses inside and creates the <next> element who's child the blocks will become
  // returns DOM of new history
  withNewBlocksAtEnd(hist,blocks){
    var o = hist.cloneNode(true);
    var lastElem = this.recurseToLastElement(o);
    var nxt = Blockly.Xml.textToDom("<next>" + Blockly.Xml.domToText(blocks) + "</next>");
    lastElem.appendChild(nxt);
    return o;
  }

  recurseToLastElement(el){
    var hasNextElement=true;
    while(hasNextElement){
      // find the next element.
      hasNextElement=false;
      for(var i=0; i<el.children.length && !hasNextElement; i++){
        hasNextElement = el.children[i].localName == "next";
        if(hasNextElement){
          el = el.children[i].children[0]; // get the block beneath the next statement
        }
      }
    }
    return el;
  }

  // color history and make history uneditable
  colorHistory() {
    var workspace = this.getWorkspace();
    //identify head block of history
    var blocks = workspace.getBlockById("history");
    if (blocks!=null){
      var blocksList = blocks.getDescendants();
      // apply Colour and set editable, movable on all blocks in the history including head block
      // Editable = false : parameters can not be changed
      // Movable = false : can't move blocks, duplication not possible!
      // TODO: blocks can still be place inbetween history blocks (and inside repeat blocks)

      blocksList.forEach(element => {
        element.setColour("#FCF1D6");
        element.setEditable(false);
        element.setMovable(false);
      });

      //find last block in history
      while(blocks.getNextBlock()!=null){
        blocks = blocks.getNextBlock();
      }

      // set as "end-block", no blocks can be attached to this block
      blocks.setNextStatement(false);
    }

  }

  // clear grayed out history
  clearHistory() {
    this.historyIsGray = false;
    this.copyableHistory = this.getInitialEmptyHistory();
    var xml = Blockly.Xml.workspaceToDom(this.getWorkspace());
    xml.removeChild(xml.children[2] /* history */);
    xml.appendChild(this.copyableHistory.cloneNode(true));
    this.loadDOMasWorkspace(xml);
  }
}
// vim: shiftwidth=2



// WEBPACK FOOTER //
// ./src/app/services/blocks.service.ts